var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { activateI18n } from "../i18n.js";
import { reset } from "../styles.js";
import { messages as daMessages } from "./locales/da/messages.mjs";
import { messages as enMessages } from "./locales/en/messages.mjs";
import { messages as fiMessages } from "./locales/fi/messages.mjs";
import { messages as nbMessages } from "./locales/nb/messages.mjs";
import { messages as svMessages } from "./locales/sv/messages.mjs";
import { wSliderStyles } from "./styles/w-slider.styles.js";
// Inspo:
//   https://css-tricks.com/multi-thumb-sliders-particular-two-thumb-case/
//   https://css-tricks.com/multi-thumb-sliders-general-case/
//   https://css-tricks.com/lets-make-a-multi-thumb-slider-that-calculates-the-width-between-thumbs/
/**
 * Parent component for sliders (both single and range sliders). Used in combination with a `<w-slider-thumb>`.
 *
 * @slot - For single sliders place a `<w-slider-thumb>` in the default slot.
 * @slot label - Label for the slider or range slider as a whole.
 * @slot description - Optional description between the label and slider.
 * @slot from - Range sliders need to place a `<w-slider-thumb>` in the from and to slots.
 * @slot to - Range sliders need to place a `<w-slider-thumb>` in the from and to slots.
 */
class WarpSlider extends LitElement {
    /** @internal */
    static { this.shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    }; }
    static { this.styles = [reset, wSliderStyles]; }
    constructor() {
        super();
        this.disabled = false;
        /**
         * Whether or not to allow values outside the range such as "Before 1950" and "2025+".
         */
        this.openEnded = false;
        /**
         * Sets the form fields and fieldset in an invalid state
         */
        this.invalid = false;
        /**
         * Ensures a child slider thumb has a value before allowing the containing form to submit
         */
        this.required = false;
        /**
         * Should only be used in special cases
         */
        this.hiddenTextfield = false;
        /** @internal */
        this._invalidMessage = "";
        /** @internal */
        this._hasInternalError = false;
        /** @internal */
        this._showError = false;
        /** @internal */
        this._tabbableElements = [];
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
    }
    #syncSliderThumbs() {
        const sliderThumbs = this.querySelectorAll("w-slider-thumb");
        let usedNamedSlots = false;
        for (const thumb of sliderThumbs.values()) {
            // Set attributes that should be in sync between slider thumbs
            thumb.min = this.edgeMin;
            thumb.max = this.edgeMax;
            thumb.step = this.step;
            thumb.suffix = this.suffix ?? "";
            thumb.required = this.required;
            thumb.labelFormatter = this.labelFormatter;
            thumb.valueFormatter = this.valueFormatter;
            thumb.tooltipFormatter = this.tooltipFormatter;
            thumb.openEnded = this.openEnded;
            thumb._hiddenTextfield = this.hiddenTextfield;
            if (!thumb.ariaLabel) {
                if (!thumb.slot) {
                    thumb.ariaLabel = this.label || null;
                }
                if (thumb.slot === "from") {
                    thumb.ariaLabel = `${this.label} min`;
                }
                if (thumb.slot === "to") {
                    thumb.ariaLabel = `${this.label} max`;
                }
            }
            if (thumb.slot === "from" || thumb.slot === "to") {
                usedNamedSlots = true;
            }
            thumb.disabled = this.disabled;
            thumb.invalid = Boolean(this.errorText);
            this.#updateActiveTrack(thumb);
        }
        // Missing a CSS-only way to detect if something is slotted in the named slots
        if (usedNamedSlots) {
            this.fieldset.style.setProperty("--active-range-inline-start-padding", "var(--w-slider-thumb-size, 28px)");
            this.fieldset.style.setProperty("--active-range-inline-end-padding", "var(--w-slider-thumb-size, 28px)");
        }
        else {
            this.fieldset.style.setProperty("--active-range-border-radius", "4px");
        }
    }
    /** @internal */
    get edgeMin() {
        return this.openEnded ? (Number(this.min) - 1).toString() : this.min;
    }
    /** @internal */
    get edgeMax() {
        return this.openEnded ? (Number(this.max) + 1).toString() : this.max;
    }
    async connectedCallback() {
        super.connectedCallback();
        await this.updateComplete;
        if (this.step) {
            this.fieldset.style.setProperty("--step", String(this.step));
        }
        if (this.min !== undefined) {
            this.fieldset.style.setProperty("--min", this.edgeMin);
        }
        if (this.max !== undefined) {
            this.fieldset.style.setProperty("--max", this.max);
        }
        if (this.markers) {
            this.fieldset.style.setProperty("--markers", String(this.markers));
        }
        if (this.openEnded) {
            this.fieldset.style.setProperty("--over-under-offset", "1");
        }
        const sliderThumbs = this.querySelectorAll("w-slider-thumb");
        const isRangeSlider = sliderThumbs.length === 2;
        if (isRangeSlider) {
            this.fieldset.style.setProperty("--range-slider-magic-pixel", "1px");
            const sliderThumbsArr = [...sliderThumbs];
            this._tabbableElements[0] = sliderThumbsArr[0].shadowRoot.querySelector("input");
            this._tabbableElements[1] = sliderThumbsArr[1].shadowRoot.querySelector("input");
            this._tabbableElements[2] = sliderThumbsArr[0].shadowRoot.querySelector("w-textfield");
            this._tabbableElements[3] = sliderThumbsArr[1].shadowRoot.querySelector("w-textfield");
        }
        else if (sliderThumbs.length === 1) {
            const sliderThumbsArr = [...sliderThumbs];
            this._tabbableElements[0] = sliderThumbsArr[0].shadowRoot.querySelector("input");
            this._tabbableElements[1] = sliderThumbsArr[0].shadowRoot.querySelector("w-textfield");
        }
        if (this.invalid && this.error) {
            this._showError = true;
        }
        this.#syncSliderThumbs();
    }
    updated(changedProperties) {
        if (changedProperties.has("disabled") ||
            changedProperties.has("required") ||
            changedProperties.has("min") ||
            changedProperties.has("step") ||
            changedProperties.has("max") ||
            changedProperties.has("suffix") ||
            changedProperties.has("labelFormatter") ||
            changedProperties.has("valueFormatter") ||
            changedProperties.has("_invalidMessage") ||
            changedProperties.has("_hasInternalError")) {
            this.#syncSliderThumbs();
        }
        if (changedProperties.has("error") || changedProperties.has("invalid")) {
            if (this.error && this.invalid) {
                this._showError = true;
            }
            else {
                this._showError = false;
            }
            this.#syncSliderThumbs();
        }
    }
    #onThumbReset(e) {
        e.stopPropagation();
        const input = e.target;
        this.#updateActiveTrack(input);
    }
    #onInput(e) {
        const input = e.target;
        this.#updateActiveTrack(input);
        const isRangeSlider = input.slot;
        if (isRangeSlider) {
            this.#doValidation();
        }
    }
    #onBlur() {
        if (this.componentHasError) {
            this._showError = true;
        }
        else {
            this._showError = false;
        }
        this.#syncSliderThumbs();
    }
    #handleKeyDown(e) {
        if (e.key === "Tab") {
            const knownFocusableElementIndex = this._tabbableElements.indexOf(e.target.shadowRoot.activeElement);
            if (knownFocusableElementIndex === -1) {
                return; // shouldn't really happen, but don't prevent anything
            }
            const direction = e.shiftKey ? -1 : +1;
            const nextFocusableElement = this._tabbableElements[knownFocusableElementIndex + direction];
            if (!nextFocusableElement) {
                return;
            }
            e.preventDefault();
            nextFocusableElement.focus();
        }
    }
    #doValidation() {
        // In a range slider changing the value in one input can change the validity
        // of the second input. Specifically, what was a value outside the mininum or
        // maximum can become inside those limits when the limits change, by changing
        // the from or to values. Check to see if a field is invalid, but should be
        // valid based on those rules.
        let from = null;
        let to = null;
        const sliderThumbs = this.querySelectorAll("w-slider-thumb");
        for (const thumb of sliderThumbs.values()) {
            if (thumb.slot === "from")
                from = thumb;
            if (thumb.slot === "to")
                to = thumb;
        }
        if (!from || !to) {
            // Not a range slider, nothing to do here.
            return;
        }
        if (!from.invalid && !to.invalid) {
            // Both are valid, nothing to do here
            return;
        }
    }
    #onSliderValidity(e) {
        e.stopPropagation();
        const didHaveInternalError = this._hasInternalError || this.invalid;
        const triggeredThumb = e.target;
        this._hasInternalError = Boolean(e.detail.invalid) || this.invalid;
        this._invalidMessage = e.detail.invalid;
        if (didHaveInternalError === true && this._hasInternalError === false) {
            const sliderThumbs = this.querySelectorAll("w-slider-thumb");
            for (const thumb of sliderThumbs.values()) {
                if (thumb !== triggeredThumb) {
                    thumb.updateFieldAfterValidation();
                }
            }
        }
    }
    #getEdgeValue(boundary, input) {
        input.value ??= this.openEnded ? "" : boundary;
        // Use boundary for CSS positioning when value is empty
        return input.value === "" ? boundary : input.value;
    }
    /**
     * We use CSS variables to fill the active track with a background color.
     */
    #updateActiveTrack(input) {
        const slotName = input.slot;
        if (!slotName) {
            this.fieldset.style.setProperty("--from", "0");
        }
        if (slotName === "from") {
            this.fieldset.style.setProperty("--from", this.#getEdgeValue(this.edgeMin, input));
        }
        if (!slotName || slotName === "to") {
            this.fieldset.style.setProperty("--to", this.#getEdgeValue(this.edgeMax, input));
        }
    }
    /** @internal */
    get componentHasError() {
        return this.invalid || this._hasInternalError;
    }
    /** @internal */
    get errorText() {
        if (!this._showError) {
            return "";
        }
        return this.error || this._invalidMessage;
    }
    render() {
        return html `
			<fieldset
				id="fieldset"
				class="w-slider"
				@input="${this.#onInput}"
				@focusout="${this.#onBlur}"
				@slidervalidity="${this.#onSliderValidity}"
				@thumbreset="${this.#onThumbReset}"
				@keydown="${this.#handleKeyDown}"
				aria-invalid="${this.errorText ? "true" : nothing}"
				?disabled="${this.disabled}"
			>
				${this.label
            ? html `<legend class="w-slider__label">
							<slot id="label" name="label">${this.label}</slot>
						</legend>`
            : html `<slot id="label" name="label"></slot>`}
				<slot class="w-slider__description" name="description"></slot>
				${this.markers ? html `<div class="w-slider__markers"></div>` : nothing}
				<div class="w-slider__range">
					<div class="w-slider__active-range"></div>
				</div>
				<slot
					class="w-slider__slider"
					@slotchange=${this.#syncSliderThumbs}
				></slot>
				<slot
					class="w-slider__slider"
					name="from"
					@slotchange=${this.#syncSliderThumbs}
				></slot>
				<slot
					class="w-slider__slider"
					name="to"
					@slotchange=${this.#syncSliderThumbs}
				></slot>
				${this.errorText
            ? html `<p class="w-slider__error" aria-describes="fieldset">
							${this.errorText}
						</p>`
            : this.helpText
                ? html `<p class="w-slider__help-text" aria-describes="fieldset">
								${this.helpText}
							</p>`
                : nothing}
			</fieldset>
		`;
    }
}
__decorate([
    property({ reflect: true })
], WarpSlider.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpSlider.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, attribute: "open-ended" })
], WarpSlider.prototype, "openEnded", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpSlider.prototype, "error", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: "help-text" })
], WarpSlider.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpSlider.prototype, "invalid", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpSlider.prototype, "required", void 0);
__decorate([
    property({ reflect: true })
], WarpSlider.prototype, "min", void 0);
__decorate([
    property({ reflect: true })
], WarpSlider.prototype, "max", void 0);
__decorate([
    property({ type: Number, reflect: true })
], WarpSlider.prototype, "markers", void 0);
__decorate([
    property({ type: Number, reflect: true })
], WarpSlider.prototype, "step", void 0);
__decorate([
    property({ reflect: true })
], WarpSlider.prototype, "suffix", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "hidden-textfield" })
], WarpSlider.prototype, "hiddenTextfield", void 0);
__decorate([
    property({ attribute: false })
], WarpSlider.prototype, "valueFormatter", void 0);
__decorate([
    property({ attribute: false })
], WarpSlider.prototype, "tooltipFormatter", void 0);
__decorate([
    property({ attribute: false })
], WarpSlider.prototype, "labelFormatter", void 0);
__decorate([
    query("fieldset")
], WarpSlider.prototype, "fieldset", void 0);
__decorate([
    state()
], WarpSlider.prototype, "_invalidMessage", void 0);
__decorate([
    state()
], WarpSlider.prototype, "_hasInternalError", void 0);
__decorate([
    state()
], WarpSlider.prototype, "_showError", void 0);
__decorate([
    state()
], WarpSlider.prototype, "_tabbableElements", void 0);
if (!customElements.get("w-slider")) {
    customElements.define("w-slider", WarpSlider);
}
export { WarpSlider };
//# sourceMappingURL=slider.js.map